import { useEffect } from 'react';

/**
 * Auto-grow textarea hook with min/max line constraints
 * @param {React.RefObject} ref - Reference to the textarea element
 * @param {string} value - The textarea value
 * @param {Object} options - Configuration options
 * @param {number} options.minLines - Minimum number of lines (default: 1)
 * @param {number} options.maxLines - Maximum number of lines (default: null, no limit)
 */
export default function useAutoGrowTextarea(ref, value, options = {}) {
  const { minLines = 1, maxLines = null } = options;

  useEffect(() => {
    if (ref.current) {
      // Reset height to auto to get accurate scrollHeight
      ref.current.style.height = 'auto';
      
      // Get computed styles to calculate line height
      const computedStyle = window.getComputedStyle(ref.current);
      const lineHeight = parseFloat(computedStyle.lineHeight) || parseFloat(computedStyle.fontSize) * 1.5;
      const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
      const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;
      const borderTop = parseFloat(computedStyle.borderTopWidth) || 0;
      const borderBottom = parseFloat(computedStyle.borderBottomWidth) || 0;
      
      // Calculate min and max heights
      const minHeight = minLines * lineHeight + paddingTop + paddingBottom + borderTop + borderBottom;
      const maxHeight = maxLines ? maxLines * lineHeight + paddingTop + paddingBottom + borderTop + borderBottom : null;
      
      // Get the natural scroll height
      const scrollHeight = ref.current.scrollHeight;
      
      // Apply constraints
      let finalHeight = scrollHeight;
      if (finalHeight < minHeight) {
        finalHeight = minHeight;
      }
      if (maxHeight && finalHeight > maxHeight) {
        finalHeight = maxHeight;
        ref.current.style.overflowY = 'auto';
      } else {
        ref.current.style.overflowY = 'hidden';
      }
      
      ref.current.style.height = `${finalHeight}px`;
    }
  }, [value, ref, minLines, maxLines]);
} 